import { FormEvent, useEffect, useState, type JSX } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";

import PasswordInput from "src/components/common/PasswordInput";
import type { AdminUser } from "src/types/userManagement";
import { passwordPolicyError } from "src/validation/password";

type ChangePasswordModalProps = {
  /** The user whose password is being changed — null hides the modal. */
  user: AdminUser | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (password: string) => void;
};

const displayName = (user: AdminUser | null): string =>
  `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() ||
  (user?.name ?? "");

export default function ChangePasswordModal({
  user,
  isSubmitting = false,
  onClose,
  onSubmit,
}: ChangePasswordModalProps): JSX.Element {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  const show = !!user;

  useEffect(() => {
    if (show) {
      setPassword("");
      setError(undefined);
    }
  }, [show]);

  const handleChange = (value: string) => {
    setPassword(value);
    if (error) setError(undefined);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const passwordError = passwordPolicyError(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    onSubmit(password);
  };

  return (
    <Modal show={show} centered onHide={isSubmitting ? undefined : onClose}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <p className="mb-0 text-muted small">
            Set a new password for <strong>{displayName(user)}</strong>. They
            will need it the next time they sign in.
          </p>
          <Form.Group controlId="change-password">
            <Form.Label className="d-block text-start mb-1">
              New Password
            </Form.Label>
            <PasswordInput
              value={password}
              onChange={handleChange}
              error={error}
              placeholder="Enter new password"
              disabled={isSubmitting}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Updating...
              </>
            ) : (
              "Update Password"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
